import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getClientDigitalTwin } from "../../../api/digitalTwinShipmentService";

export default function ClientDigitalTwinPage() {
    const navigate = useNavigate();

    const [shipmentId, setShipmentId] = useState("");
    const [twin, setTwin] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [trackingNumber, setTrackingNumber] = useState("");
    const [liveStatus, setLiveStatus] = useState("Disconnected");
    const [livePosition, setLivePosition] = useState(null);

    const loadTwin = async () => {
        try {
            setLoading(true);
            setError("");
            setTwin(null);

            if (!shipmentId || Number(shipmentId) <= 0) {
                setError("Shipment ID is required.");
                return;
            }

            const data = await getClientDigitalTwin(shipmentId);
            setTwin(data);

            setTrackingNumber(
                data?.shipment?.trackingNumber ||
                data?.trackingNumber ||
                ""
            );
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load digital twin."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!trackingNumber) return;

        const apiUrl =
            import.meta.env.VITE_API_URL || "https://api.idlogichain.com";

        const wsUrl = apiUrl
            .replace("https://", "wss://")
            .replace("http://", "ws://");

        const socket = new WebSocket(
            `${wsUrl}/ws/iot?tracking=${encodeURIComponent(trackingNumber)}`
        );

        setLiveStatus("Connecting");

        socket.onopen = () => setLiveStatus("Connected");

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === "iot_update") {
                    setLivePosition({
                        latitude: data.latitude,
                        longitude: data.longitude,
                        updatedAt: data.updatedAt,
                        autoStatus: data.autoStatus,
                        eta: data.eta,
                    });

                    if (shipmentId) loadTwin();
                }
            } catch {
                // ignore invalid message
            }
        };

        socket.onerror = () => setLiveStatus("Error");
        socket.onclose = () => setLiveStatus("Disconnected");

        return () => socket.close();
    }, [trackingNumber]);

    const latestTelemetry = useMemo(() => {
        const list = twin?.telemetryRecords || [];
        return [...list].sort(
            (a, b) => new Date(b.recordedAt || 0) - new Date(a.recordedAt || 0)
        )[0];
    }, [twin]);

    const latestSecurity = useMemo(() => {
        const list = twin?.securityStatuses || [];
        return [...list].sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        )[0];
    }, [twin]);

    const trustScore = Number(twin?.trustScore ?? 0);

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        Shipment Digital Twin
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Live virtual representation of shipment state, trust,
                        telemetry and security status.
                    </p>
                </div>

                <button
                    onClick={() => navigate("/client/iot-tracking")}
                    className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-semibold hover:bg-indigo-700 transition"
                >
                    Open IoT Tracking
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-3">
                    <input
                        type="number"
                        value={shipmentId}
                        onChange={(e) => setShipmentId(e.target.value)}
                        placeholder="Shipment ID"
                        className="w-full md:w-64 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <button
                        onClick={loadTwin}
                        disabled={loading}
                        className="rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                    >
                        {loading ? "Loading..." : "View Digital Twin"}
                    </button>

                    {twin && (
                        <button
                            onClick={loadTwin}
                            className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                        >
                            Refresh
                        </button>
                    )}
                </div>

                {error && (
                    <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                        {error}
                    </div>
                )}
            </div>

            {twin && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                        <StatCard
                            title="Trust Score"
                            value={`${trustScore}%`}
                            subtitle="Adaptive trust evaluation"
                            color={trustScore >= 80 ? "green" : trustScore >= 60 ? "yellow" : "red"}
                        />

                        <StatCard
                            title="Risk Level"
                            value={twin.riskLevel ?? "�"}
                            subtitle="Current security risk"
                            color={String(twin.riskLevel).toLowerCase() === "low" ? "green" : "yellow"}
                        />

                        <StatCard
                            title="Shipment Status"
                            value={twin.currentStatus || "�"}
                            subtitle={`Shipment ID: ${twin.shipmentId}`}
                            color="blue"
                        />

                        <StatCard
                            title="WebSocket"
                            value={liveStatus}
                            subtitle={trackingNumber || "No tracking number"}
                            color={liveStatus === "Connected" ? "green" : "slate"}
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow border border-slate-200 p-6">
                            <h2 className="text-2xl font-bold mb-5">
                                Twin Summary
                            </h2>

                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <InfoRow label="Shipment ID" value={twin.shipmentId} />
                                <InfoRow label="Tracking Number" value={trackingNumber || "�"} />
                                <InfoRow label="Status" value={twin.currentStatus || "�"} />
                                <InfoRow label="Risk Level" value={twin.riskLevel ?? "�"} />
                                <InfoRow label="Anomaly Detected" value={twin.hasAnomaly ? "Yes" : "No"} />
                                <InfoRow label="Requires Inspection" value={twin.requiresInspection ? "Yes" : "No"} />
                                <InfoRow
                                    label="Last Updated"
                                    value={
                                        twin.lastUpdatedAt
                                            ? new Date(twin.lastUpdatedAt).toLocaleString()
                                            : "�"
                                    }
                                />
                            </div>

                            <div className="mt-6">
                                <div className="flex justify-between mb-2">
                                    <span className="font-semibold">Trust Score</span>
                                    <span className="font-bold">{trustScore}%</span>
                                </div>

                                <div className="w-full h-4 bg-slate-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${trustScore >= 80
                                            ? "bg-green-500"
                                            : trustScore >= 60
                                                ? "bg-yellow-500"
                                                : "bg-red-500"
                                            }`}
                                        style={{ width: `${Math.min(trustScore, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
                            <h2 className="text-2xl font-bold mb-5">
                                Live Tracking
                            </h2>

                            <InfoRow label="WebSocket" value={liveStatus} />
                            <InfoRow label="Tracking" value={trackingNumber || "�"} />

                            {livePosition ? (
                                <div className="mt-4 space-y-3">
                                    <InfoRow label="Latitude" value={livePosition.latitude ?? "�"} />
                                    <InfoRow label="Longitude" value={livePosition.longitude ?? "�"} />
                                    <InfoRow label="Status" value={livePosition.autoStatus || "�"} />
                                    <InfoRow
                                        label="Updated"
                                        value={
                                            livePosition.updatedAt
                                                ? new Date(livePosition.updatedAt).toLocaleString()
                                                : "�"
                                        }
                                    />
                                </div>
                            ) : (
                                <p className="text-slate-500 mt-4">
                                    Waiting for live IoT updates...
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                        <DataCard title="Latest Telemetry">
                            {latestTelemetry ? (
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <InfoRow label="Latitude" value={latestTelemetry.latitude ?? "�"} />
                                    <InfoRow label="Longitude" value={latestTelemetry.longitude ?? "�"} />
                                    <InfoRow label="IoT Status" value={latestTelemetry.iotStatus || "�"} />
                                    <InfoRow
                                        label="Recorded At"
                                        value={
                                            latestTelemetry.recordedAt
                                                ? new Date(latestTelemetry.recordedAt).toLocaleString()
                                                : "�"
                                        }
                                    />
                                </div>
                            ) : (
                                <EmptyText text="No telemetry records available." />
                            )}
                        </DataCard>

                        <DataCard title="Latest Security Status">
                            {latestSecurity ? (
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <InfoRow label="GeoFence" value={latestSecurity.geoFenceStatus || "�"} />
                                    <InfoRow label="Blockchain" value={latestSecurity.blockchainStatus || "�"} />
                                    <InfoRow label="Route" value={latestSecurity.routeStatus || "�"} />
                                    <InfoRow label="ETA" value={latestSecurity.etaStatus || "�"} />
                                    <InfoRow
                                        label="Created At"
                                        value={
                                            latestSecurity.createdAt
                                                ? new Date(latestSecurity.createdAt).toLocaleString()
                                                : "�"
                                        }
                                    />
                                </div>
                            ) : (
                                <EmptyText text="No security records available." />
                            )}
                        </DataCard>
                    </div>

                    <DataCard title="Telemetry History">
                        {twin.telemetryRecords?.length > 0 ? (
                            <ResponsiveTable
                                columns={["Latitude", "Longitude", "IoT Status", "Recorded At"]}
                                rows={twin.telemetryRecords.map((t) => [
                                    t.latitude ?? "�",
                                    t.longitude ?? "�",
                                    t.iotStatus || "�",
                                    t.recordedAt
                                        ? new Date(t.recordedAt).toLocaleString()
                                        : "�",
                                ])}
                            />
                        ) : (
                            <EmptyText text="No telemetry records available." />
                        )}
                    </DataCard>

                    <div className="mt-6">
                        <DataCard title="Security History">
                            {twin.securityStatuses?.length > 0 ? (
                                <ResponsiveTable
                                    columns={["GeoFence", "Blockchain", "Route", "ETA", "Created At"]}
                                    rows={twin.securityStatuses.map((s) => [
                                        s.geoFenceStatus || "�",
                                        s.blockchainStatus || "�",
                                        s.routeStatus || "�",
                                        s.etaStatus || "�",
                                        s.createdAt
                                            ? new Date(s.createdAt).toLocaleString()
                                            : "�",
                                    ])}
                                />
                            ) : (
                                <EmptyText text="No security records available." />
                            )}
                        </DataCard>
                    </div>
                </>
            )}
        </div>
    );
}

const StatCard = ({ title, value, subtitle, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        yellow: "bg-yellow-50 text-yellow-700",
        red: "bg-red-50 text-red-700",
        slate: "bg-slate-50 text-slate-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-2xl ${colors[color]}`}>
                {value}
            </div>
            <p className="text-slate-500 text-sm mt-3">{subtitle}</p>
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            {label}
        </p>
        <p className="font-bold text-slate-900 mt-1 break-words">
            {value}
        </p>
    </div>
);

const DataCard = ({ title, children }) => (
    <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
        <h2 className="text-2xl font-bold mb-5">{title}</h2>
        {children}
    </div>
);

const EmptyText = ({ text }) => (
    <p className="text-slate-500">{text}</p>
);

const ResponsiveTable = ({ columns, rows }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-sm">
            <thead className="bg-slate-100">
                <tr>
                    {columns.map((c) => (
                        <th key={c} className="text-left px-4 py-3">
                            {c}
                        </th>
                    ))}
                </tr>
            </thead>

            <tbody>
                {rows.map((row, index) => (
                    <tr key={index} className="border-t hover:bg-slate-50">
                        {row.map((cell, i) => (
                            <td key={i} className="px-4 py-3">
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);